import { motion, useReducedMotion } from 'framer-motion';

export interface ProgressRingProps {
  /** Value 0..100 */
  pct: number;
  /** Outer SVG diameter in px */
  size?: number;
  /** Stroke width in px */
  stroke?: number;
  /** Tailwind text-* class for the stroke color (uses currentColor) */
  colorClass?: string;
  /** Track (background) Tailwind class */
  trackClass?: string;
  /** Optional center label */
  children?: React.ReactNode;
}

/**
 * Cluster M v0.5.0 — circular progress ring rendered as a real SVG arc
 * (stroke-dasharray on a circle), not a rectangle bar. framer-motion animates
 * the offset on mount and on pct change. Respects prefers-reduced-motion.
 */
export function ProgressRing({
  pct,
  size = 120,
  stroke = 10,
  colorClass = 'text-amber-500',
  trackClass = 'text-slate-200',
  children,
}: ProgressRingProps) {
  const reduce = useReducedMotion();
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.max(0, Math.min(100, pct));
  const offset = circumference * (1 - clamped / 100);

  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={stroke}
          className={trackClass}
          stroke="currentColor"
        />
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={stroke}
          strokeLinecap="round"
          className={colorClass}
          stroke="currentColor"
          strokeDasharray={circumference}
          initial={reduce ? { strokeDashoffset: offset } : { strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: offset }}
          transition={reduce ? { duration: 0 } : { duration: 0.9, ease: 'easeOut' }}
        />
      </svg>
      {children && (
        <div className="absolute inset-0 flex items-center justify-center">{children}</div>
      )}
    </div>
  );
}
